import React, { useEffect, useState, useCallback } from 'react';
import { emailService, OutboundEmailRow } from '../services/emailService';
import { getReconnectErrorMeta } from '../utils/emailErrors';
import { useAuth } from '../contexts/AuthContext';

type Props =
  | {
      clientId: string;
      leadId?: never;
      refreshKey?: number;
      onLoaded?: (rowCount: number) => void;
      onEditScheduled?: (row: OutboundEmailRow) => void;
    }
  | {
      leadId: string;
      clientId?: never;
      refreshKey?: number;
      onLoaded?: (rowCount: number) => void;
      onEditScheduled?: (row: OutboundEmailRow) => void;
    };

const ContactEmailHistory: React.FC<Props> = (props) => {
  const clientId = 'clientId' in props ? props.clientId : undefined;
  const leadId = 'leadId' in props ? props.leadId : undefined;
  const refreshKey = props.refreshKey ?? 0;
  const onLoaded = props.onLoaded;
  const onEditScheduled = props.onEditScheduled;
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [rows, setRows] = useState<OutboundEmailRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reconnectRequired, setReconnectRequired] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setReconnectRequired(false);
      const res = await emailService.listHistory(
        clientId ? { client_id: clientId } : { lead_id: leadId! }
      );
      const list = (res as { results?: OutboundEmailRow[] }).results ?? [];
      setRows(list);
      setError('');
      onLoaded?.(list.length);
    } catch (e) {
      if (getReconnectErrorMeta(e).requiresReconnect) {
        setReconnectRequired(true);
      }
      setError(e instanceof Error ? e.message : 'Failed to load email history');
      onLoaded?.(0);
    } finally {
      setLoading(false);
    }
  }, [clientId, leadId, onLoaded]);

  const handleCancelScheduled = async (row: OutboundEmailRow) => {
    if (!window.confirm('Cancel this scheduled email?')) return;
    try {
      setActionLoadingId(row.id);
      setReconnectRequired(false);
      await emailService.cancelScheduledEmail(row.id);
      await load();
    } catch (e) {
      if (getReconnectErrorMeta(e).requiresReconnect) {
        setReconnectRequired(true);
        setError('Reconnect your mailbox in Settings → Email before cancelling scheduled sends.');
      } else {
        setError(e instanceof Error ? e.message : 'Failed to cancel scheduled email');
      }
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleRetryFailed = async (row: OutboundEmailRow) => {
    if (!window.confirm('Retry sending this email?')) return;
    try {
      setActionLoadingId(row.id);
      setReconnectRequired(false);
      await emailService.retryFailedEmail(row.id);
      await load();
    } catch (e) {
      if (getReconnectErrorMeta(e).requiresReconnect) {
        setReconnectRequired(true);
        setError('Reconnect your mailbox in Settings → Email before retrying failed sends.');
      } else {
        setError(e instanceof Error ? e.message : 'Failed to retry email');
      }
    } finally {
      setActionLoadingId(null);
    }
  };

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  if (!clientId && !leadId) {
    return null;
  }

  if (loading) {
    return <p className="text-sm text-fg-muted py-2">Loading email history…</p>;
  }
  if (error) {
    return (
      <div className="space-y-2 py-2">
        <p className="text-sm text-danger-fg">{error}</p>
        {reconnectRequired ? (
          <p className="text-xs text-amber-700 dark:text-amber-300 rounded-lg bg-amber-50 dark:bg-amber-950/30 px-2 py-2">
            Mailbox permissions need to be refreshed. Reconnect in{' '}
            <a href="/settings/email" className="underline font-medium">
              Settings → Email
            </a>
            .
          </p>
        ) : null}
      </div>
    );
  }
  if (!rows.length) {
    return (
      <p className="text-sm text-fg-muted py-2 border border-dashed border-border rounded-lg px-3 py-4 text-center">
        No emails sent from LaunchPoint yet.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {reconnectRequired ? (
        <p className="text-xs text-amber-700 dark:text-amber-300 rounded-lg bg-amber-50 dark:bg-amber-950/30 px-2 py-2">
          Mailbox permissions need to be refreshed. Reconnect in{' '}
          <a href="/settings/email" className="underline font-medium">
            Settings → Email
          </a>
          .
        </p>
      ) : null}
      <ul className="space-y-3 max-h-56 overflow-y-auto pr-1">
        {rows.map((r) => (
          <li
            key={r.id}
            className="rounded-lg border border-border bg-surface px-3 py-2 text-sm"
          >
          <div className="flex justify-between gap-2">
            <span className="font-medium text-fg truncate">{r.subject}</span>
            <span className="text-fg-muted shrink-0 text-xs">
              {r.sent_at ? new Date(r.sent_at).toLocaleString() : new Date(r.created_at).toLocaleString()}
            </span>
          </div>
          <p className="text-fg-secondary text-xs mt-1">
            To {r.related_contact_email}
            {r.sender_email ? ` · from ${r.sender_email}` : ''}
            {r.status === 'failed' ? (
              <span className="text-danger-fg ml-1">Failed</span>
            ) : null}
            {r.status === 'scheduled' && r.scheduled_send_at ? (
              <span className="text-blue-600 dark:text-blue-400 ml-1">
                Scheduled for {new Date(r.scheduled_send_at).toLocaleString()}
              </span>
            ) : null}
            {r.status === 'cancelled' ? (
              <span className="text-fg-muted ml-1">Cancelled</span>
            ) : null}
          </p>
          {r.status === 'scheduled' ? (
            <div className="mt-2 flex gap-2">
              {onEditScheduled ? (
                <button
                  type="button"
                  onClick={() => onEditScheduled(r)}
                  disabled={actionLoadingId === r.id}
                  className="rounded-md border border-border px-2 py-1 text-xs text-fg hover:bg-surface-muted disabled:opacity-50"
                >
                  Edit
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => handleCancelScheduled(r)}
                disabled={actionLoadingId === r.id}
                className="rounded-md border border-danger-fg/40 px-2 py-1 text-xs text-danger-fg hover:bg-danger-fg/10 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          ) : null}
          {r.status === 'failed' && isAdmin ? (
            <div className="mt-2 flex gap-2">
              <button
                type="button"
                onClick={() => handleRetryFailed(r)}
                disabled={actionLoadingId === r.id}
                className="rounded-md border border-border px-2 py-1 text-xs text-fg hover:bg-surface-muted disabled:opacity-50"
              >
                Retry
              </button>
            </div>
          ) : null}
          {r.body && (
            <p className="text-fg-muted text-xs mt-1 line-clamp-2">{r.body.replace(/\s+/g, ' ').trim()}</p>
          )}
          {r.failure_reason && (
            <p className="text-danger-fg text-xs mt-1">{r.failure_reason}</p>
          )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ContactEmailHistory;
